import { v4 as uuidv4 } from 'uuid';
import { saveCampaignSession } from './campaignTracking';
import { subscribe, emit, events } from './eventManager.js';

// Initialize window._mtm (data layer) immediately, preserving window._mtm if it already exists
window._mtm = window._mtm || [];

// Function called from a Matomo Tag Manager tag after Segment is loaded to move items from
// mtmPreLoadDataLayer to _mtm so Segment calls can be made
window.__dequeueMTM = function () {
    if (window.mtmPreLoadDataLayer && Array.isArray(window.mtmPreLoadDataLayer)) {
        window.mtmPreLoadDataLayer.forEach(payload => {
            window._mtm.push(payload);
        });
        window.mtmPreLoadDataLayer = [];
    }
    window.segmentLoaded = true;
};

function pushPayload(payload) {
    if (window.segmentLoaded) {
        const dataLayer = window._mtm;
        const previousLength = dataLayer.length;
        dataLayer.push(payload);
        return dataLayer.slice(previousLength).includes(payload);
    } else {
        window.mtmPreLoadDataLayer.push(payload);
        return false;
    }
}

export const pusher = payload => {
    pushPayload(payload);
};

/**
 * Function called from a Matomo Tag Manager tag whenever consent status is available or updated.
 * @param {object} consent The consent object with example below
 *  {
 *      action: 'consent_updated',
 *      type: 'explicit',
 *      preferences: {
 *          essential: false,
 *          functional: false,
 *          marketing: false,
 *          performance: false,
 *      },
 *  }
 */
window.__setConsent = function (consent) {
    setConsent(consent);
};

function setConsent(consent, error = false) {
    emit(events.CONSENT, consent, error);
}

function trackEvent(eventName, eventProperties) {
    pushPayload({ eventProperties: undefined });
    return pushPayload({ event: 'track', eventName, eventProperties, hitId: uuidv4() });
}

export const analytics = {
    get: key => {
        const dataLayer = window._mtm;
        if (!Array.isArray(dataLayer)) return undefined;
        for (let index = dataLayer.length - 1; index >= 0; index--) {
            const entry = dataLayer[index];
            if (entry && Object.prototype.hasOwnProperty.call(entry, key)) return entry[key];
        }
        return undefined;
    },
    isReady: () => window.segmentLoaded === true && Array.isArray(window._mtm),
    identify: (userId, userProperties) => {
        pusher({ userProperties: undefined });
        const payload = { event: 'identify', userProperties, hitId: uuidv4() };
        if (userId !== undefined && userId !== null) {
            payload.userId = userId;
        }
        pusher(payload);
    },
    page: (pageCategory, pageName, pageProperties) => {
        pusher({ pageProperties: undefined });
        pusher({ event: 'page', pageCategory, pageName, pageProperties, hitId: uuidv4() });
    },
    subscribeToConsent(callback) {
        return subscribe(events.CONSENT, callback);
    },
    track: (eventName, eventProperties) => { trackEvent(eventName, eventProperties); },
    trackAndVerify: trackEvent,
};

export function pushVisitorToMTM(visitor) {
    if (visitor && (visitor.ip || visitor.country || visitor.isBot !== null || visitor.isInternal !== null)) {
        window._mtm.push({
            event: 'visitor',
            visitorIpAddress: visitor.ip ?? '',
            visitorCountry: visitor.country ?? '',
            visitorIsBot: visitor.isBot ?? false,
            visitorIsInternal: visitor.isInternal ?? false,
        });
    }
}

/**
 * Push region information to MTM
 * @param {Array} region
 * @example for user selection "ASEAN": ['BN', 'KH', 'ID', 'LV', 'MY', 'MM', 'PH', 'SG', 'TH', 'VN']
 * for user selection "United States": ['US']
 */
export function pushRegionToMTM(region) {
    if (Array.isArray(region) && region.length > 0) {
        window._mtm.push({
            event: 'region',
            visitorRegion: region,
        });
    }
}

/**
 * Load the external Matomo Tag Manager javascript configured for our sites
 * @param {string} containerId Matomo Tag Manager container id
 * @example UFefytkf_dev_7adf05bd8a6ab9900b8297d9
 */
export function loadMTMModule(containerId) {
    let _mtm = window._mtm;
    _mtm.push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' });
    (function () {
        let d = document,
            g = d.createElement('script'),
            s = d.getElementsByTagName('script')[0];
        g.async = true;
        g.src = `https://metrics.siemens-cdn.com/js/container_${containerId}.js`;
        s.parentNode.insertBefore(g, s);
    })();
}

export function appendChildToBody(element) {
    if (document.body) {
        document.body.appendChild(element);
    } else {
        const bodyLoadedObserver = new MutationObserver(() => {
            if (document.body) {
                document.body.appendChild(element);
                bodyLoadedObserver.disconnect();
            }
        });
        bodyLoadedObserver.observe(document.documentElement, { childList: true });
    }
}

export function loadCookieBanner(version, settingsId) {
    const script = document.createElement('script');
    script.id = 'usercentrics-cmp';
    script.setAttribute('data-version', version);
    script.setAttribute('data-settings-id', settingsId);
    script.src = `https://data.cdn.siemens.com/?settingsId=${settingsId}`;
    script.async = true;
    appendChildToBody(script);
}

export const mtmLoader = (account, config, callback) => {
    if (account && account.sid) {
        if (!window.mtmLoaded) {
            // Create campaign session only on first mtmLoader call
            saveCampaignSession({ sessionId: account.sid, userId: account.user?.contact_id });
            window._mtm.push({ sessionId: account.sid });
        }
        // allow userId to be pushed after Matomo Tag Manager load because anonymous users can be assigned
        // a contact_id over the course of a session
        if (account.user && account.user.contact_id) {
            window._mtm.push({ userId: account.user.contact_id });
        }

        // If the cookiesDisabled boolean is defined, push it to the dataLayer
        if (typeof account.cookiesDisabled !== 'undefined') {
            window._mtm.push({ cookiesDisabled: account.cookiesDisabled });
        }

        if (!window.mtmLoaded) {
            window._mtm.push({ initialPageHitId: uuidv4() });
            callback(config.containerId);
            window.mtmLoaded = true;
        }
    }
};

/**
 * Initialize Matomo Tag Manager
 * @param {object} config config.mtm from disw.init
 * @param {string} config.containerId Matomo container id
 * @param {string | undefined} config.pageId The contentful page entry ID
 * @param {boolean} [config.sessionRequested=true] whether disw.account was initialized and will emit the session
 * @param {function} callback optional callback used for initializing Matomo Tag Manager. a default callback is used
 */
export async function initializeMTM(config = {}, callback = loadMTMModule) {
    /* Initialize config variable and apply default values */
    // eslint-disable-next-line no-prototype-builtins
    if (!config.hasOwnProperty('containerId')) {
        throw new Error('config.containerId is required');
    }
    config.cookieBannerEnabled = config.cookieBannerEnabled ?? true;
    config.cookieBannerSettingsId = config.cookieBannerSettingsId || 'GXj83XOdm';
    config.cookieBannerVersion = config.cookieBannerVersion || 'latest';
    config.sessionRequested = config.sessionRequested ?? true;

    /* Initialize variable to prevent Matomo Tag Manager from being loaded each time an account event is emitted.
     * sid does not change so we don't want to push it on the dataLayer each time an event is emitted.
     * contact_id can change in the case where an anonymous user is assigned a contact_id in the session.
     */
    window.mtmLoaded = false;

    /* Initialize variable to determine when to push data to the _mtm dataLayer making sure that Segment scripts are
     * loaded before pushing any data. If you push too soon, Matomo won't be listening to the dataLayer and the items
     * won't trigger Segment calls.
     */
    window.segmentLoaded = false;

    // Put page, identify, and track calls here until session and contact_id are pushed and Segment is loaded
    window.mtmPreLoadDataLayer = [];

    // Initialize a lightweight analytics object that manages segment methods
    window.analytics = analytics;

    let visitorIsBot = null;
    let cookieBannerLoaded = false;

    // Push DSA header values to _mtm immediately because they are needed to determine if tags will trigger
    // eslint-disable-next-line no-undef
    disw.account.subscribeToVisitor(visitor => {
        visitorIsBot = visitor?.isBot ?? null;
        pushVisitorToMTM(visitor);
    });

    // eslint-disable-next-line no-undef
    disw.region.subscribeToRegion(pushRegionToMTM);

    // If we have a pageId, push it to the data layer
    if (typeof config.pageId !== 'undefined') {
        window._mtm.push({ pageId: config.pageId });
        window.analytics.pageId = config.pageId;
    }

    const loadCookieBannerOnce = () => {
        if (config.cookieBannerEnabled && !cookieBannerLoaded) {
            loadCookieBanner(config.cookieBannerVersion, config.cookieBannerSettingsId);
            cookieBannerLoaded = true;
        }
    };

    // Without a session request no account event will ever arrive, so the bot status stays unknown
    if (!config.sessionRequested) {
        loadCookieBannerOnce();
    }

    // The account event is emitted once the session request settles, whether it succeeded or failed, and the visitor
    // event (emitted only on success) always precedes it. Anything other than a confirmed bot gets the consent
    // framework (UserCentrics), so a missing bot status can never withhold the banner from a real user.
    // Only after we get a sessionId can we load Matomo Tag Manager
    // eslint-disable-next-line no-undef
    disw.account.subscribeToAccount(account => {
        if (visitorIsBot === true) {
            return;
        }
        loadCookieBannerOnce();
        mtmLoader(account, config, callback);
    });
}
